/**
 * Custom hook for handling Backstage signals during provisioning
 */
import { useEffect, useState } from 'react';
import { useSignal } from '@backstage/plugin-signals-react';
import { SignalType, ProvisioningComponent } from '../types';
import { ApplicationResponse } from '@devxdiscover/devhub-common';

/**
 * Hook for handling signal updates during provisioning
 * @param updateProvisioningComponent - Callback to update component status
 * @returns Current task ID and setter
 */
export const useSignalHandling = (
  updateProvisioningComponent: (
    event: string,
    status: ProvisioningComponent['status'],
    progress: number,
    signal?: any,
  ) => void,
) => {
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);

  // Hook to handle provisioning updates via Backstage signals
  const { lastSignal } =
    useSignal<SignalType<ApplicationResponse>>('devhub-channel');

  useEffect(() => {
    if (!currentTaskId || !lastSignal) return;

    try {
      const task = lastSignal;

      // Only process messages for our specific taskId
      if (task.taskId !== currentTaskId) return;

      // eslint-disable-next-line no-console
      console.log('OnboardingModal: Signal update:', task);

      // Skip pending signals to avoid resetting progress
      // (progress is already set when API accepts the request)
      if (task.status === 'pending') {
        return;
      }

      // Map status to progress value:
      // - in-progress: 50% (show activity without dropping progress)
      // - completed: 100%
      // - failed: 0% (but status will prevent further updates)
      let progress = 0;
      if (task.status === 'in-progress') {
        progress = 50;
      } else if (task.status === 'completed') {
        progress = 100;
      } else if (task.status === 'skipped') {
        progress = 100;
      }

      updateProvisioningComponent(task.event, task.status, progress, task);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing signal message:', error);
    }
  }, [lastSignal, currentTaskId, updateProvisioningComponent]);

  return {
    currentTaskId,
    setCurrentTaskId,
  };
};
